import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import RotatingBanner from "@/components/Common/RotatingBanner";
import CategoryChips from "@/components/Common/CategoryChips";
import Section from "@/components/Common/Section";
import ProductCard from "@/components/Product/ProductCard";
import ShopCard from "@/components/Shop/ShopCard";
import InlineAd, { injectInlineAds } from "@/components/Common/InlineAd";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

export default function Home() {
  const [categories, setCategories] = useState([]);
  const [feed, setFeed] = useState({ recent_products: [], video_products: [], trending_products: [], verified_shops: [], promoted_shops: [], recommended_products: [], recommended_shops: [] });
  const [favIds, setFavIds] = useState([]);
  const [favShopIds, setFavShopIds] = useState([]);
  const [inlineAds, setInlineAds] = useState([]);
  const nav = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    api.get("/meta/categories").then(r => setCategories(r.data || [])).catch(() => {});
    api.get("/products/feed/home").then(r => setFeed(r.data || {})).catch(() => {});
    api.get("/ads/inline", { params: { placement: "home" } }).then(r => setInlineAds(r.data || [])).catch(() => {});
  }, [user?.id]);

  useEffect(() => {
    if (!user) { setFavIds([]); setFavShopIds([]); return; }
    api.get("/favorites/ids").then(r => {
      setFavIds(r.data?.product_ids || []);
      setFavShopIds(r.data?.shop_ids || []);
    }).catch(() => {});
  }, [user]);

  const toggleFav = async (id) => {
    if (!user) { toast.error("Connectez-vous pour ajouter aux favoris"); nav("/connexion"); return; }
    try {
      const { data } = await api.post("/favorites/toggle", { target_id: id, target_type: "product" });
      setFavIds((ids) => data.favorited ? [...ids, id] : ids.filter(x => x !== id));
      toast.success(data.favorited ? "Ajouté aux favoris" : "Retiré des favoris");
    } catch {}
  };

  const toggleFavShop = async (id) => {
    if (!user) { toast.error("Connectez-vous pour suivre une boutique"); nav("/connexion"); return; }
    try {
      const { data } = await api.post("/favorites/toggle", { target_id: id, target_type: "shop" });
      setFavShopIds((ids) => data.favorited ? [...ids, id] : ids.filter(x => x !== id));
      toast.success(data.favorited ? "Boutique ajoutée aux favoris" : "Retirée des favoris");
    } catch {}
  };

  const renderGrid = (items) => (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
      {injectInlineAds(items, inlineAds).map((it, i) =>
        it?.__ad ? (
          <InlineAd key={`ad-${i}-${it.ad.id}`} ad={it.ad} />
        ) : (
          <ProductCard key={it.id} product={it} onFavorite={toggleFav} isFav={favIds.includes(it.id)} />
        )
      )}
    </div>
  );

  const renderShopRow = (shops) => (
    <div className="flex gap-3 overflow-x-auto scrollbar-hide -mx-1 px-1 pb-2">
      {shops.map(s => (
        <ShopCard key={s.id} shop={s} isFav={favShopIds.includes(s.id)} onFavorite={toggleFavShop} />
      ))}
    </div>
  );

  return (
    <div className="pb-8 space-y-6 sm:space-y-8">
      <CategoryChips categories={categories} />
      <RotatingBanner />

      {user && feed.recommended_products?.length > 0 && (
        <Section title="Recommandé pour vous" subtitle="Inspiré de vos visites et favoris" actionLabel="Tout voir" onAction={() => nav("/recherche?sort=score")}>
          {renderGrid(feed.recommended_products)}
        </Section>
      )}

      {user && feed.recommended_shops?.length > 0 && (
        <Section title="Boutiques susceptibles de vous plaire" subtitle="Proches de vos préférences">
          {renderShopRow(feed.recommended_shops)}
        </Section>
      )}

      {feed.verified_shops.length > 0 && (
        <Section title="Boutiques vérifiées" subtitle="Vendeurs de confiance" actionLabel="Voir tout" onAction={() => nav("/recherche?verified=1")}>
          {renderShopRow(feed.verified_shops)}
        </Section>
      )}

      <Section title="Produits récents" subtitle="Les dernières annonces" actionLabel="Tout voir" onAction={() => nav("/recherche?sort=recent")}>
        {renderGrid(feed.recent_products)}
        {feed.recent_products.length === 0 && <EmptyFeed />}
      </Section>

      {feed.video_products.length > 0 && (
        <Section title="Produits en vidéo" subtitle="Découvrez en mouvement" actionLabel="Tout voir" onAction={() => nav("/recherche?video=1")}>
          {renderGrid(feed.video_products)}
        </Section>
      )}

      {feed.promoted_shops.length > 0 && (
        <Section title="Boutiques à la une">
          {renderShopRow(feed.promoted_shops)}
        </Section>
      )}

      {feed.trending_products.length > 0 && (
        <Section title="Tendances" subtitle="Ce qui cartonne en ce moment">
          {renderGrid(feed.trending_products)}
        </Section>
      )}
    </div>
  );
}

function EmptyFeed() {
  return (
    <div className="rounded-2xl border border-dashed border-[#E8E6E1] p-8 text-center">
      <div className="text-sm text-[#73706B]">Aucun produit pour l'instant. Soyez le premier à publier !</div>
    </div>
  );
}
